/**
 * Accessibility Utilities - ARIA, focus management, and screen reader support
 */

// Focus management
export function focusElement(element, options = {}) {
  if (!element) return
  const { preventScroll = false, focusVisible = true } = options
  
  try {
    if (focusVisible) {
      element.focus({ preventScroll })
    } else {
      element.focus({ preventScroll })
      // Remove focus ring if not needed
      element.dataset.focusVisible = 'false'
    }
  } catch (err) {
    // Silently fail
  }
}

export function trapFocus(container, options = {}) {
  const { returnFocus = true } = options
  let previousFocus = document.activeElement

  const handleKeyDown = (event) => {
    if (event.key !== 'Tab') return
    
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    
    if (focusable.length === 0) return
    
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  document.addEventListener('keydown', handleKeyDown)

  return () => {
    document.removeEventListener('keydown', handleKeyDown)
    if (returnFocus && previousFocus) {
      previousFocus.focus()
    }
  }
}

// ARIA helpers
export function getAriaLabel(label, description) {
  return description ? `${label}. ${description}` : label
}

export function getAriaDescribedBy(id, description) {
  return description ? `${id}-description` : undefined
}

// Role helpers
export function getRole(component) {
  const roles = {
    button: 'button',
    link: 'link',
    heading: 'heading',
    list: 'list',
    listitem: 'listitem',
    dialog: 'dialog',
    alert: 'alert',
    status: 'status',
    navigation: 'navigation',
    main: 'main',
    complementary: 'complementary',
    contentinfo: 'contentinfo',
  }
  return roles[component] || undefined
}

// Screen reader announcements
let announceTimer = null

export function announce(message, priority = 'polite') {
  if (typeof document === 'undefined') return

  const announcer = document.getElementById('announcer')
  if (!announcer) {
    // Create announcer if it doesn't exist
    const el = document.createElement('div')
    el.id = 'announcer'
    el.setAttribute('aria-live', priority)
    el.setAttribute('aria-atomic', 'true')
    el.style.position = 'absolute'
    el.style.width = '1px'
    el.style.height = '1px'
    el.style.padding = '0'
    el.style.margin = '-1px'
    el.style.overflow = 'hidden'
    el.style.clip = 'rect(0, 0, 0, 0)'
    el.style.whiteSpace = 'nowrap'
    el.style.border = '0'
    document.body.appendChild(el)
  }

  const announcerEl = document.getElementById('announcer')
  if (!announcerEl) return

  // Clear previous announcement
  if (announceTimer) {
    clearTimeout(announceTimer)
    announcerEl.textContent = ''
  }

  // Announce after a brief delay to ensure previous announcement is cleared
  announceTimer = setTimeout(() => {
    announcerEl.textContent = message
  }, 100)
}

// Skip to content link
export function createSkipLink(targetId = 'main-content') {
  return `<a href="#${targetId}" class="sr-only focus:not-sr-only">Skip to main content</a>`
}

// Keyboard indicators
export function showKeyboardFocus() {
  if (typeof document === 'undefined') return

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      document.body.classList.add('keyboard-navigation')
      document.body.classList.remove('mouse-navigation')
    }
  })

  document.addEventListener('mousedown', () => {
    document.body.classList.add('mouse-navigation')
    document.body.classList.remove('keyboard-navigation')
  })
}

// Reduced motion
export function prefersReducedMotion() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Color contrast check (basic)
export function hasGoodContrast(color1, color2) {
  // Simple luminance calculation
  function getLuminance(hex) {
    const rgb = hex.replace('#', '').match(/.{2}/g)
    if (!rgb) return 0
    
    const [r, g, b] = rgb.map(c => parseInt(c, 16) / 255)
    const srgb = [r, g, b].map(c => 
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    )
    return srgb[0] * 0.2126 + srgb[1] * 0.7152 + srgb[2] * 0.0722
  }

  try {
    const l1 = getLuminance(color1)
    const l2 = getLuminance(color2)
    const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
    return ratio >= 4.5 // WCAG AA standard
  } catch {
    return true // Assume good contrast
  }
}

// Helper to add ARIA attributes to components
export function addAriaProps(props, aria) {
  const ariaProps = Object.keys(aria).reduce((acc, key) => {
    if (key.startsWith('aria-') || key === 'role') {
      acc[key] = aria[key]
    } else {
      acc[`aria-${key}`] = aria[key]
    }
    return acc
  }, {})
  
  return { ...props, ...ariaProps }
}

export default {
  focusElement,
  trapFocus,
  getAriaLabel,
  getAriaDescribedBy,
  getRole,
  announce,
  createSkipLink,
  showKeyboardFocus,
  prefersReducedMotion,
  hasGoodContrast,
  addAriaProps,
}